import { AfterViewChecked, ChangeDetectorRef, Directive, ElementRef, Input, OnDestroy } from '@angular/core';
import { SgDocTitle } from './doc-title.service';

// Directive logic taken from https://github.com/ngx-translate/core/blob/master/projects/ngx-translate/core/src/lib/translate.directive.ts

function isDefined(value: any): boolean {
  return typeof value !== 'undefined' && value !== null;
}

@Directive({
  selector: '[sgDocTitle]',
  standalone: false,
})
export class SgDocTitleDirective implements AfterViewChecked, OnDestroy {
  title: string;
  private initialTitle: string;

  @Input() set sgDocTitle(title: string) {
    if (title) {
      this.title = title;
      this.checkNodes();
    }
  }

  constructor(
    private titleService: SgDocTitle,
    private element: ElementRef,
    private cdr: ChangeDetectorRef
  ) {
    this.initialTitle = titleService.getTitle();
  }

  ngAfterViewChecked() {
    this.checkNodes();
  }

  ngOnDestroy(): void {
    this.titleService.setTitle(this.initialTitle);
  }

  checkNodes(forceUpdate = false) {
    let nodes: NodeList = this.element.nativeElement.childNodes;
    // if the element is empty
    if (!nodes.length) {
      // we add the key as content
      this.setContent(this.element.nativeElement, this.title);
      nodes = this.element.nativeElement.childNodes;
    }
    for (let i = 0; i < nodes.length; ++i) {
      let node: any = nodes[i];
      if (node.nodeType === 3) {
        // node type 3 is a text node
        let key: string;
        if (this.title) {
          key = this.title;
          if (forceUpdate) {
            node.lastKey = null;
          }
        } else {
          let content = this.getContent(node);
          let trimmedContent = content.trim();
          if (trimmedContent.length) {
            // we want to use the content as a key, not the translation value
            if (content !== node.currentValue) {
              key = trimmedContent;
              // the content was changed from the user, we'll use it as a reference if needed
              node.originalContent = this.getContent(node);
            } else if (node.originalContent && forceUpdate) {
              // the content seems ok, but the lang has changed
              node.lastKey = null;
              // the current content is the translation, not the key, use the last real content as key
              key = node.originalContent.trim();
            }
          }
        }
        this.updateValue(key, node);
        return;
      }
    }

    // if we're here and we have a title it means that the host of the directive have multiple complex children and not a simple text expression.
    if (this.title) {
      this.titleService.setTitle(this.title);
    }
  }

  updateValue(key: string, node: any) {
    if (key) {
      if (node.lastKey !== key) {
        node.lastKey = key;
      }
      if (!node.originalContent) {
        node.originalContent = this.getContent(node);
      }
      node.currentValue = isDefined(key) ? key : node.originalContent;
      // we replace in the original content to preserve spaces that we might have trimmed
      this.setContent(node, this.title ? node.currentValue : node.originalContent.replace(key, node.currentValue));
      this.cdr.markForCheck();
    }
  }

  getContent(node: any): string {
    return isDefined(node.textContent) ? node.textContent : node.data;
  }

  setContent(node: any, content: string): void {
    if (isDefined(node.textContent)) {
      node.textContent = content;
    } else {
      node.data = content;
    }
    this.titleService.setTitle(content || '');
  }
}
